/**
 * API-backed organization hooks.
 *
 * These call the Dashboard API clinical-directory contract
 * (`GET /patients/{patientId}/directory`) through the typed
 * openapi-fetch client. Directory identity aggregates are mapped
 * into the client `Organization` shape; fields the directory does
 * not model (address details, description, imagery, pricing…)
 * are left unset rather than fabricated.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { Organization, OrganizationType } from "../providers.types";

type DirectorySearchOperation = paths["/patients/{patientId}/directory"]["get"];
type DirectorySearchQuery = NonNullable<
  DirectorySearchOperation["parameters"]["query"]
>;
type DirectorySearchResponse =
  DirectorySearchOperation["responses"]["200"]["content"]["application/json"];
type DirectoryIdentity = DirectorySearchResponse["data"]["items"][number];
type DirectoryOrganizationIdentity = Extract<
  DirectoryIdentity,
  { kind: "organization" }
>;
type DirectoryDetailResponse =
  paths["/patients/{patientId}/directory/{entityId}"]["get"]["responses"]["200"]["content"]["application/json"];

export type DirectoryRole = NonNullable<DirectorySearchQuery["role"]>;
export type DirectoryStatus = NonNullable<DirectorySearchQuery["status"]>;

const ROLE_TO_ORGANIZATION_TYPE: Partial<
  Record<DirectoryRole, OrganizationType>
> = {
  fertility_clinic: "fertility-clinic",
  medical_practice: "medical-practice",
  laboratory: "lab",
  pharmacy: "pharmacy",
  donor_agency: "donor-agency",
  surrogacy_agency: "surrogacy-agency",
  wellness_provider: "wellness-provider",
};

function activeContactPoint(
  identity: DirectoryOrganizationIdentity,
  type: DirectoryOrganizationIdentity["contactPoints"][number]["type"],
) {
  const candidates = identity.contactPoints.filter(
    (point) => point.type === type,
  );
  return (
    candidates.find((point) => point.validTo == null)?.value ??
    candidates[0]?.value
  );
}

/**
 * Map a directory organization aggregate into the client Organization
 * shape. Returns null when none of the identity's roles is
 * representable as an OrganizationType.
 */
export function directoryIdentityToOrganization(
  identity: DirectoryOrganizationIdentity,
): Organization | null {
  const type = identity.roles
    .map((role) => ROLE_TO_ORGANIZATION_TYPE[role.role])
    .find((mapped) => mapped != null);
  if (!type) return null;

  const specialties = identity.classifications
    .filter((classification) => classification.type === "specialty")
    .map((classification) => classification.display ?? classification.code);

  return {
    id: identity.clinicalEntityId,
    externalIds: {},
    type,
    name: identity.displayName,
    website: activeContactPoint(identity, "website"),
    phone: activeContactPoint(identity, "phone"),
    email: activeContactPoint(identity, "email"),
    address: {
      line1: "",
      city: "",
      state: "",
      zip: "",
      country: identity.countryCode ?? "",
    },
    specialties: specialties.length ? specialties : undefined,
    _enrichment: {
      status: "seed-only",
      sections: {},
    },
    _meta: {
      createdAt: identity.createdAt,
      updatedAt: identity.updatedAt,
    },
  };
}

// ─── Query key factories ────────────────────────────────────────────────────

export const organizationApiKeys = {
  all: ["organizations-api"] as const,
  list: (params?: OrganizationSearchParams) =>
    [...organizationApiKeys.all, "list", params] as const,
  detail: (id: string) => [...organizationApiKeys.all, "detail", id] as const,
};

// ─── Parameter types ────────────────────────────────────────────────────────

export interface OrganizationSearchParams {
  q?: string;
  role?: DirectoryRole;
  countryCode?: string;
  status?: DirectoryStatus;
  limit?: number;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Search directory organizations for the authenticated patient.
 * Use this when you need server-side search/filter (e.g. pharmacy directory).
 */
export function useOrganizationsApi(
  params?: OrganizationSearchParams,
  options?: { enabled?: boolean },
) {
  const { client, patientId } = useApiClient();

  return useQuery({
    queryKey: organizationApiKeys.list(params),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/directory",
        {
          params: {
            path: { patientId: patientId! },
            query: {
              kind: "organization",
              q: params?.q,
              role: params?.role,
              countryCode: params?.countryCode,
              status: params?.status,
              limit: params?.limit,
            },
          },
        },
      );

      if (error) {
        throwApiError(
          error,
          "Failed to search the provider directory",
          "provider-directory",
          response.status,
        );
      }

      const items = data?.data.items ?? [];
      const organizations = items
        .filter(
          (item): item is DirectoryOrganizationIdentity =>
            item.kind === "organization",
        )
        .map(directoryIdentityToOrganization)
        .filter((organization) => organization != null);

      return {
        data: organizations,
        nextCursor: data?.data.nextCursor ?? null,
      };
    },
    enabled: (options?.enabled ?? true) && !!patientId,
  });
}

/**
 * Fetch a single directory organization by its clinical entity id.
 */
export function useOrganizationApi(organizationId: string | null) {
  const { client, patientId } = useApiClient();

  return useQuery({
    queryKey: organizationApiKeys.detail(organizationId ?? ""),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/directory/{entityId}",
        {
          params: {
            path: { patientId: patientId!, entityId: organizationId! },
          },
        },
      );

      if (error) {
        throwApiError(
          error,
          "Failed to load the directory organization",
          "provider-directory",
          response.status,
        );
      }

      const identity: DirectoryDetailResponse["data"] | undefined = data?.data;
      if (!identity || identity.kind !== "organization") return null;
      return directoryIdentityToOrganization(identity);
    },
    enabled: !!organizationId && !!patientId,
  });
}
